import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Users,
  CheckCircle,
  XCircle,
  Search,
  Download,
  RefreshCw,
} from 'lucide-react';
import api, { eventAPI } from '../../../services/api';
import { showToast } from '../../../utils/toasts';
import { format } from 'date-fns';

const EventRegistrations = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<any>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0,
    members: 0,
    guests: 0,
    attended: 0,
    registered: 0,
    cancelled: 0,
  });

  useEffect(() => {
    if (id) {
      fetchEventDetails();
      fetchRegistrations();
    }
  }, [id, currentPage]);

  useEffect(() => {
    handleSearch();
  }, [searchQuery, registrations]);

  useEffect(() => {
    // Update stats
    setStats({
      total: registrations.length,
      members: registrations.filter(r => r.registrantType === 'member').length,
      guests: registrations.filter(r => r.registrantType === 'guest').length,
      attended: registrations.filter(r => r.status === 'attended').length,
      registered: registrations.filter(r => r.status === 'registered').length,
      cancelled: registrations.filter(r => r.status === 'cancelled').length,
    });
  }, [registrations]);

  const fetchEventDetails = async () => {
    try {
      const response = await eventAPI.getEvent(id!);
      setEvent(response.data.data?.event);
    } catch (error: any) {
      showToast.error('Failed to load event details');
      console.error('Error:', error);
    }
  };

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/event-registrations/event/${id}`, {
        params: {
          page: currentPage,
          limit: 20,
        },
      });

      const data = response.data.data;
      setRegistrations(data.registrations || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (error: any) {
      showToast.error('Failed to load registrations');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchQuery.trim()) {
      setFilteredRegistrations(registrations);
      return;
    }

    const filtered = registrations.filter((record) => {
      const name = record.type === 'member'
        ? `${record.member?.fullName || `${record.member?.firstName} ${record.member?.lastName}`}`.toLowerCase()
        : `${record.guest?.firstName} ${record.guest?.lastName}`.toLowerCase();

      const phone = record.type === 'member'
        ? record.member?.phone || ''
        : record.guest?.phone || '';

      const email = record.type === 'member'
        ? record.member?.email || ''
        : record.guest?.email || '';

      return name.includes(searchQuery.toLowerCase()) ||
        phone.includes(searchQuery) ||
        email.includes(searchQuery.toLowerCase());
    });

    setFilteredRegistrations(filtered);
  };

  const getStatusBadge = (status: string) => {
    const baseClass = 'px-3 py-1 rounded-full text-xs font-semibold';
    switch (status) {
      case 'attended':
        return `${baseClass} bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400`;
      case 'cancelled':
        return `${baseClass} bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400`;
      default:
        return `${baseClass} bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400`;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'attended':
        return <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />;
      default:
        return null;
    }
  };

  const formatDate = (timestamp: string) => {
    return format(new Date(timestamp), 'MMM dd, yyyy HH:mm');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading registrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-8xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/events/${id}`)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Event Registrations
                </h1>
                {event && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {event.title}
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={fetchRegistrations}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button
                onClick={() => {
                  /* Export to CSV */
                }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Export"
              >
                <Download className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-8xl mx-auto py-8 px-4 sm:px-6 lg:px-4">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 sm:gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Registrations</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.total}
                </p>
              </div>
              <div className="p-3 bg-primary-100 dark:bg-primary-900/20 rounded-lg">
                <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Members</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.members}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Guests</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {stats.guests}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Attended</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                  {stats.attended}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Registered</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                  {stats.registered}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Cancelled</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-2">
                  {stats.cancelled}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center space-x-2 bg-gray-50 dark:bg-gray-700 px-4 py-3 rounded-lg">
            <Search className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-0 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 w-full"
            />
          </div>
        </div>

        {/* Registrations Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          {filteredRegistrations.length > 0 ? (
            <>
              {/* Mobile Card List */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-600">
                {filteredRegistrations.map((registration) => (
                  <div key={registration._id} className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                          {registration.type === 'member'
                            ? registration.member?.firstName?.[0]?.toUpperCase() || 'M'
                            : registration.guest?.firstName?.[0]?.toUpperCase() || 'G'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {registration.registrantType === 'member'
                                ? registration.member?.fullName || `${registration.member?.firstName} ${registration.member?.lastName}`
                                : `${registration.guest?.firstName} ${registration.guest?.lastName}`}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">
                              {registration.registrantType === 'member' ? 'Member' : 'Guest'}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {getStatusIcon(registration.status)}
                            <span className={getStatusBadge(registration.status)}>
                              {registration.status.charAt(0).toUpperCase() + registration.status.slice(1)}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                          {registration.registrantType === 'member'
                            ? registration.member?.phone || 'N/A'
                            : registration.guest?.phone || registration.contactPhone || 'N/A'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 truncate">
                          {registration.registrantType === 'member'
                            ? registration.member?.email || ''
                            : registration.guest?.email || ''}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>Registered: {formatDate(registration.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs">
                          <span className="text-gray-600 dark:text-gray-400">
                            Attendees: <span className="font-semibold text-gray-900 dark:text-gray-100">{registration.numberOfAttendees || 1}</span>
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            Attended: <span className="font-semibold text-gray-900 dark:text-gray-100">{registration.attendanceCount || 0}</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Phone/Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Registered On
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Number of Attendees
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Attended Count
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                  {filteredRegistrations.map((registration) => (
                    <tr
                      key={registration._id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/20 flex items-center justify-center">
                            <span className="text-sm font-semibold text-primary-600 dark:text-primary-400">
                              {registration.type === 'member'
                                ? registration.member?.firstName?.[0]?.toUpperCase() || 'M'
                                : registration.guest?.firstName?.[0]?.toUpperCase() || 'G'}  {/* Show initial based on registrantType */}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {registration.registrantType === 'member'
                                ? registration.member?.fullName || `${registration.member?.firstName} ${registration.member?.lastName}`
                                : `${registration.guest?.firstName} ${registration.guest?.lastName}`}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {registration.registrantType === 'member' ? 'Member' : 'Guest'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <p className="truncate">
                            {registration.registrantType === 'member'
                              ? registration.member?.phone || 'N/A'
                              : registration.guest?.phone || registration.contactPhone || 'N/A'}
                          </p>
                          <p className="truncate text-xs text-gray-500 dark:text-gray-500">
                            {registration.registrantType === 'member'
                              ? registration.member?.email || ''
                              : registration.guest?.email || ''}
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(registration.status)}
                          <span className={getStatusBadge(registration.status)}>
                            {registration.status.charAt(0).toUpperCase() +
                              registration.status.slice(1)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(registration.createdAt)}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {registration.numberOfAttendees || 1}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {registration.attendanceCount || 0}
                        </p>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery ? 'No registrations found matching your search' : 'No registrations yet'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventRegistrations;
